import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface ReportingManager {
  firstName?: string;
  lastName?: string;
  email?: string;
  isDeleted?: boolean;
}

export interface OrgBrief {
  _id?: string;
  name?: string;
}

export interface DepartmentBrief {
  _id?: string;
  name?: string;
}

export interface Director {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  org?: OrgBrief;
  department?: DepartmentBrief;
  reportingManager?: ReportingManager;
}

export type DirectorsResponse = {
  success: boolean;
  message?: string;
  count?: number;
  data: Director[];
};

const fetchDirectors = async (orgId: string): Promise<DirectorsResponse> => {
  const { data } = await customAxios.get<DirectorsResponse>(
    `/users/organization/${orgId}/directors`,
  );
  return data;
};

export const useGetDirectors = (orgId?: string | null) =>
  useQuery<DirectorsResponse, AxiosError>(
    ["directors", orgId],
    () => {
      if (!orgId) throw new Error("No organization id provided");
      return fetchDirectors(orgId);
    },
    {
      enabled: !!orgId,
      onError: (error) =>
        showNotification({
          title: "Error Loading Directors",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load directors",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    },
  );
